/**
 * Analysis configuration types shared between frontend and backend.
 * Single source of truth for the shape of analysis config passed between client ↔ server.
 *
 * AnalysisConfig represents the user's pick generation preferences.
 * All fields are optional since configs can be partially specified.
 */

import type { AnalysisMode } from './analysisMode.ts';

// =============================================================================
// Sub-config interfaces
// =============================================================================

export interface PlayerStatsConfig {
  l5Average?: boolean;
  l10Average?: boolean;
  l20Average?: boolean;
  seasonAverage?: boolean;
}

export interface AdvancedStatsConfig {
  usageRate?: boolean;
  trueShootingPct?: boolean;
  assistRatio?: boolean;
  offensiveRating?: boolean;
  defensiveRating?: boolean;
  /** Include each team's last-10 points scored and allowed against its earlier average (BallDontLie scores). Default: false. */
  l10TeamRatings?: boolean;
}

export interface MatchupConfig {
  playerVsTeamHistory?: boolean;
  playerPosition?: boolean;
}

export interface OpponentDefenseConfig {
  defVsPosition?: boolean;
  reboundingAllowed?: boolean;
  threePointDefense?: boolean;
  pointsInPaint?: boolean;
  fastBreakAllowed?: boolean;
}

export interface GameScriptConfig {
  blowoutRisk?: boolean;
  backToBack?: boolean;
  paceImpact?: boolean;
  gameTotal?: boolean;
  homeAwaySplits?: boolean;
  revengeGame?: boolean;
  recentForm?: boolean;
}

export interface OtherFactorsConfig {
  /** @deprecated Auto-derived from 2+ stat windows. Kept for backwards compat. */
  trendAnalysis?: boolean;
  historySource?: 'personal' | 'community' | 'off';
  injuryStatus?: boolean;
  usageTrend?: boolean;
  minutesTrend?: boolean;
  efficiencyTrend?: boolean;
  /** Include sharp money signals (Pinnacle lines, +EV, line movement) in AI prompt. Elite and admin. Default: false. */
  includeSharpContext?: boolean;
  /** Include projected starting lineups in AI prompt (MSF lineups only). Default: false. */
  includeProjectedStarters?: boolean;
  /** Include team prop hit rate signals in AI prompt. Admin only. Default: false. */
  includeTeamPropSignals?: boolean;
}

export interface OutputOptionsConfig {
  analysisMode?: AnalysisMode;
  [key: string]: boolean | string | undefined;
}

export interface PropTypesConfig {
  // Game markets
  h2h?: boolean;
  spreads?: boolean;
  totals?: boolean;
  alternate_spreads?: boolean;
  alternate_totals?: boolean;
  // Team markets
  team_totals?: boolean;
  alternate_team_totals?: boolean;
  // Basic player props
  player_points?: boolean;
  player_rebounds?: boolean;
  player_assists?: boolean;
  player_threes?: boolean;
  player_blocks?: boolean;
  player_steals?: boolean;
  player_turnovers?: boolean;
  // Shooting props
  player_field_goals?: boolean;
  player_frees_made?: boolean;
  // Combo props
  player_blocks_steals?: boolean;
  player_points_rebounds_assists?: boolean;
  player_points_rebounds?: boolean;
  player_points_assists?: boolean;
  player_rebounds_assists?: boolean;
  // Special props
  player_double_double?: boolean;
  player_triple_double?: boolean;
  player_first_basket?: boolean;
  player_first_team_basket?: boolean;
  // Alternate player props
  player_points_alternate?: boolean;
  player_rebounds_alternate?: boolean;
  player_assists_alternate?: boolean;
  player_threes_alternate?: boolean;
  player_steals_alternate?: boolean;
  player_blocks_alternate?: boolean;
  player_turnovers_alternate?: boolean;
  player_field_goals_alternate?: boolean;
  player_blocks_steals_alternate?: boolean;
  player_points_rebounds_assists_alternate?: boolean;
  player_points_rebounds_alternate?: boolean;
  player_points_assists_alternate?: boolean;
  player_rebounds_assists_alternate?: boolean;
  // Half game markets
  h2h_h1?: boolean;
  h2h_h2?: boolean;
  spreads_h1?: boolean;
  spreads_h2?: boolean;
  totals_h1?: boolean;
  totals_h2?: boolean;
  team_totals_h1?: boolean;
  team_totals_h2?: boolean;
  alternate_spreads_h1?: boolean;
  alternate_spreads_h2?: boolean;
  alternate_totals_h1?: boolean;
  alternate_totals_h2?: boolean;
  alternate_team_totals_h1?: boolean;
  alternate_team_totals_h2?: boolean;
  // Quarter game markets
  h2h_q1?: boolean;
  h2h_q2?: boolean;
  h2h_q3?: boolean;
  h2h_q4?: boolean;
  spreads_q1?: boolean;
  spreads_q2?: boolean;
  spreads_q3?: boolean;
  spreads_q4?: boolean;
  totals_q1?: boolean;
  totals_q2?: boolean;
  totals_q3?: boolean;
  totals_q4?: boolean;
  team_totals_q1?: boolean;
  team_totals_q2?: boolean;
  team_totals_q3?: boolean;
  team_totals_q4?: boolean;
  // Q1 player props
  player_points_q1?: boolean;
  player_rebounds_q1?: boolean;
  player_assists_q1?: boolean;
  [key: string]: boolean | undefined;
}

/** Whether a generation's market list selects this market: an empty or missing list selects every market. */
export function isMarketEnabled(
  propTypes: PropTypesConfig | undefined,
  marketKey: string,
): boolean {
  if (!propTypes || Object.keys(propTypes).length === 0) return true;
  return propTypes[marketKey] === true;
}

export interface BanListConfig {
  includeBannedPlayers?: boolean;
  [key: string]: boolean | undefined;
}

// ── Per-league ban list helpers ───────────────────────────────────────────────

/** Per-league banned players map: { basketball_nba: ["Player A", ...], basketball_wnba: ["Player B", ...] } */
export type BannedPlayersMap = Record<string, string[]>;

/** Normalize banned players from DB — handles both old string[] format and new per-league map */
export function normalizeBannedPlayers(raw: unknown): BannedPlayersMap {
  if (!raw) return {};
  if (Array.isArray(raw)) {
    // A flat string[] is how ban lists were stored before they were per league; that
    // legacy data is read as the NBA list. It reads old rows only and is no precedent
    // for defaulting a missing league anywhere else.
    return { basketball_nba: raw.filter((p): p is string => typeof p === 'string') };
  }
  if (typeof raw === 'object') {
    return raw as BannedPlayersMap;
  }
  return {};
}

/** Get banned players for a specific league from the map */
export function getBannedPlayersForLeague(map: BannedPlayersMap, leagueKey: string): string[] {
  return map[leagueKey] || [];
}

export interface EfficiencyMetricsConfig {
  bettingValueEfficiency?: boolean;
  lineValueDetection?: boolean;
  historicalTrends?: boolean;
}

export interface ParlayStrategyConfig {
  minLegCount?: number;
  maxLegCount?: number;
  targetOddsMin?: number;
  targetOddsMax?: number;
  directionPreference?: 'balanced' | 'over' | 'under';
}

export const GENERATION_MODES = ['llm', 'ml', 'hybrid', 'optimized', 'shadow'] as const;

export type GenerationMode = (typeof GENERATION_MODES)[number];

export const DEFAULT_GENERATION_MODE: GenerationMode = 'shadow';

/**
 * Whether a generation in this mode always sends the standard LLM prompt, and so whether a
 * prompt-content setting (e.g. team prop signals) affects it. `ml` and `hybrid` send it only
 * when they fall back to LLM generation.
 */
export function sendsStandardPrompt(mode: GenerationMode | undefined): boolean {
  const resolved = mode ?? DEFAULT_GENERATION_MODE;
  return resolved === 'llm' || resolved === 'shadow' || resolved === 'optimized';
}

export interface MlConfig {
  /** Generation mode; absent means DEFAULT_GENERATION_MODE. */
  generationMode?: GenerationMode;
  /** Max props to select per hybrid/ML run. Default 8 (from ML_CONFIG_BOUNDS). */
  maxLegsPerRun?: number;
}

export type LegScoringStrategy =
  | 'ml_light_trend' // ML*0.3 + trend + boosts — Q1 72%, +36pp spread (BEST)
  | 'trend_boosted' // Trend + contextual boosts, no ML — Q1 64%, +22pp delta
  | 'opp_boosted_ml' // ML + opponent hit rate — legacy default
  | 'ml_score'; // Pure ML score — baseline

/** Config for the Under + Moderate Favorite hard filter */
export interface UnderModFavoriteFilter {
  /** Whether to reject Under bets on moderate favorites (default: true) */
  enabled: boolean;
  /** Minimum spread (inclusive) that triggers rejection (default: -6.5) */
  spreadMin: number;
  /** Maximum spread (inclusive) that triggers rejection (default: -1) */
  spreadMax: number;
}

export interface ParlayIntelligenceConfig {
  /** Leg scoring strategy for selecting and ranking legs (default: 'opp_boosted_ml') */
  legScoringStrategy?: LegScoringStrategy;
  /** Max legs to select from the full generation pool (4-8, default 6) */
  maxLegsToConsider?: number;
  /** Max anchor legs — high-confidence legs that appear in multiple parlays (2-3, default 2) */
  maxAnchorLegs?: number;
  /** Max times any single leg can appear across all parlays (default 2) */
  maxLegReuse?: number;
  /** Max 2-leg parlays to build. Default: floor(maxLegsToConsider / 2). */
  maxTwoLegParlays?: number;
  /** Number of 3-leg parlays to build (0-4, default 2). Must not duplicate any 2-leg combo. */
  threeLegParlays?: number;
  /** Include a lottery parlay (4-6 legs). Default false. */
  includeLotteryParlay?: boolean;
  /**
   * Use the brute-force constructor instead of the default greedy round-robin.
   * Enumerates every valid 2-leg and 3-leg combination and picks the
   * highest-scoring ones, which lets a top-scored leg anchor multiple 2-leg
   * parlays — a pattern the greedy constructor structurally can't produce.
   * Slightly slower; hard-capped at 10 legs considered. Default false.
   */
  useBruteForce?: boolean;

  // ── Hard Filters (pre-scoring rejection) ──
  // Data-validated against 101 real FanDuel bets (3/23 - 4/6/2026)

  /** Reject Under bets when player's team is moderate favorite.
   *  Data: 17% hit rate (2/12), +38pp impact. Default: enabled, spread [-6.5, -1]. */
  rejectUnderModFavorite?: UnderModFavoriteFilter;
  /** Reject Under bets on away players. Data: 23% hit rate (3/13), +31pp impact. Default: true. */
  rejectUnderAway?: boolean;
  /** Reject Over bets on B2B players. Data: 0% hit rate (0/3), +52pp impact. Default: true. */
  rejectB2bOver?: boolean;
  /** Reject Cold trend + non-Strong/Good edge. Data: 14% hit rate (1/7), -36pp. Default: true. */
  rejectColdWeakEdge?: boolean;

  // ── Post-Score Modifiers ──

  /** Boost for Over + moderate favorite (spread -1 to -6.5). Data: 64% hit rate. Default: 3. */
  overModFavoriteBoost?: number;
  /** Boost for Over Assists props. Data: 62% hit rate. Default: 2. */
  overAssistsBoost?: number;
  /** Boost for Over Rebounds props. Data: 60% hit rate. Default: 1. */
  overReboundsBoost?: number;
  /** Boost for Hot trend legs. Data: 56% hit rate. Default: 2. */
  hotTrendBoost?: number;
}

// =============================================================================
// Main AnalysisConfig interface
// =============================================================================

/**
 * Analysis configuration for pick generation.
 * All sub-configs are optional since they can be partially specified.
 */
export interface AnalysisConfig {
  playerStats?: PlayerStatsConfig;
  advancedStats?: AdvancedStatsConfig;
  matchupData?: MatchupConfig;
  opponentDefense?: OpponentDefenseConfig;
  gameScript?: GameScriptConfig;
  otherFactors?: OtherFactorsConfig;
  outputOptions?: OutputOptionsConfig;
  propTypes?: PropTypesConfig;
  banList?: BanListConfig;
  efficiencyMetrics?: EfficiencyMetricsConfig;
  parlayStrategy?: ParlayStrategyConfig;
  ml?: MlConfig;
  parlayIntelligence?: ParlayIntelligenceConfig;
}
